import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertCircle,
  FiBookOpen,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiCode,
  FiDownload,
  FiEdit2,
  FiFileText,
  FiGrid,
  FiHeart,
  FiLoader,
  FiMap,
  FiPlus,
  FiRepeat,
  FiSettings,
  FiShield,
  FiStar,
  FiTrash2,
  FiX,
  FiZap,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import { EDUCATIONAL } from "../config/apiConfig";
import "./EducationalAreaPage.css";

// ─────────────────────────────────────────────
// Icon map (iconName vem da API como string)
// ─────────────────────────────────────────────

const ICON_MAP = {
  FiZap,
  FiStar,
  FiHeart,
  FiGrid,
  FiBriefcase,
  FiFileText,
  FiShield,
  FiMap,
  FiRepeat,
  FiCode,
};

const DEFAULT_CATEGORIES = [
  "Cultura",
  "Institucional",
  "Crédito",
  "Operações",
  "Estratégia",
  "Vendas",
];

// Valor sentinela do <select> que revela o campo de nova categoria
const NEW_CATEGORY = "__new__";

const LEVELS = ["Fundamental", "Intermediário", "Avançado"];

const ICON_OPTIONS = Object.keys(ICON_MAP);

const EMPTY_FORM = {
  name: "",
  description: "",
  category: DEFAULT_CATEGORIES[0],
  newCategory: "",
  level: LEVELS[0],
  iconName: "FiFileText",
};

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const normalizeSections = (raw) =>
  raw.map((s) => ({ ...s, docs: s.documents ?? [] }));

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────

export default function EducationalAreaPage() {
  const [sections, setSections] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [apiError, setApiError] = useState(null);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Todos");
  const [expandedId, setExpandedId] = useState(null);

  // Manage state
  const [isManaging, setIsManaging] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [titleInput, setTitleInput] = useState("");
  const [editingDoc, setEditingDoc] = useState(null);
  const [docInput, setDocInput] = useState("");
  const [uploadingSection, setUploadingSection] = useState(null);

  // Create / delete section state
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingNew, setSavingNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingSection, setDeletingSection] = useState(null);

  // ── Load sections ──
  const fetchSections = useCallback(async () => {
    try {
      setLoadingPage(true);
      setPageError(null);
      const { data } = await axiosInstance.get(EDUCATIONAL.GET_SECTIONS);
      setSections(normalizeSections(data.data));
    } catch {
      setPageError("Não foi possível carregar as trilhas. Tente novamente.");
    } finally {
      setLoadingPage(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  // Auto-dismiss API errors
  useEffect(() => {
    if (!apiError) return;
    const t = setTimeout(() => setApiError(null), 4000);
    return () => clearTimeout(t);
  }, [apiError]);

  const featured = sections.find((s) => s.featured) ?? null;

  // Categorias = padrões + as que já existem nas trilhas cadastradas
  const categoryOptions = useMemo(() => {
    const set = new Set(DEFAULT_CATEGORIES);
    sections.forEach((s) => {
      const cat = (s.category ?? "").trim();
      if (cat) set.add(cat);
    });
    return [...set];
  }, [sections]);

  const categories = useMemo(() => ["Todos", ...categoryOptions], [categoryOptions]);

  const grid = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sections.filter((s) => {
      if (featured && s.id === featured.id) return false;
      if (category !== "Todos" && s.category !== category) return false;
      if (!term) return true;
      return (
        s.name.toLowerCase().includes(term) ||
        (s.description ?? "").toLowerCase().includes(term) ||
        s.docs.some((d) => d.displayName.toLowerCase().includes(term))
      );
    });
  }, [search, category, sections, featured]);

  const visibleCount = grid.length + (featured ? 1 : 0);
  const toggle = (id) => setExpandedId((prev) => (prev === id ? null : id));

  // ── Title editing ──
  const startEditTitle = (section) => {
    setEditingTitle(section.id);
    setTitleInput(section.name);
  };

  const saveTitle = async (sectionId) => {
    const newName = titleInput.trim();
    setEditingTitle(null);
    if (!newName) return;

    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, name: newName } : s))
    );

    try {
      await axiosInstance.put(EDUCATIONAL.UPDATE_SECTION(sectionId), { name: newName });
    } catch {
      setApiError("Não foi possível salvar o título.");
      fetchSections();
    }
  };

  // ── Section create ──
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreating(true);
  };

  const cancelCreate = () => {
    setCreating(false);
    setForm(EMPTY_FORM);
  };

  const createSection = async () => {
    const name = form.name.trim();
    if (!name || savingNew) return;

    const category =
      form.category === NEW_CATEGORY ? form.newCategory.trim() : form.category;
    if (!category) {
      setApiError("Informe o nome da nova categoria.");
      return;
    }

    setSavingNew(true);
    try {
      const { data } = await axiosInstance.post(EDUCATIONAL.CREATE_SECTION, {
        name,
        description: form.description.trim(),
        category,
        level: form.level,
        iconName: form.iconName,
      });
      setSections((prev) => [...prev, ...normalizeSections([data.data])]);
      setCategory("Todos");
      setSearch("");
      cancelCreate();
    } catch {
      setApiError("Não foi possível criar a trilha.");
    } finally {
      setSavingNew(false);
    }
  };

  // ── Section delete ──
  const deleteSection = async (sectionId) => {
    setConfirmDelete(null);
    setDeletingSection(sectionId);

    const snapshot = sections;
    setSections((prev) => prev.filter((s) => s.id !== sectionId));
    if (expandedId === sectionId) setExpandedId(null);

    try {
      await axiosInstance.delete(EDUCATIONAL.DELETE_SECTION(sectionId));
    } catch {
      setApiError("Não foi possível excluir a trilha.");
      setSections(snapshot);
    } finally {
      setDeletingSection(null);
    }
  };

  // ── Doc name editing ──
  const startEditDoc = (doc) => {
    setEditingDoc(doc.id);
    setDocInput(doc.displayName);
  };

  const saveDoc = async (sectionId, docId) => {
    const newName = docInput.trim();
    setEditingDoc(null);
    if (!newName) return;

    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, docs: s.docs.map((d) => (d.id === docId ? { ...d, displayName: newName } : d)) }
          : s
      )
    );

    try {
      await axiosInstance.put(EDUCATIONAL.UPDATE_DOCUMENT(docId), { displayName: newName });
    } catch {
      setApiError("Não foi possível renomear o arquivo.");
      fetchSections();
    }
  };

  // ── Doc delete ──
  const deleteDoc = async (sectionId, docId) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, docs: s.docs.filter((d) => d.id !== docId) }
          : s
      )
    );

    try {
      await axiosInstance.delete(EDUCATIONAL.DELETE_DOCUMENT(docId));
    } catch {
      setApiError("Não foi possível remover o arquivo.");
      fetchSections();
    }
  };

  // ── File upload ──
  const handleFileUpload = async (sectionId, file) => {
    setUploadingSection(sectionId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axiosInstance.post(
        EDUCATIONAL.UPLOAD_DOCUMENT(sectionId),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId ? { ...s, docs: [...s.docs, data.data] } : s
        )
      );
    } catch {
      setApiError("Não foi possível fazer o upload do arquivo.");
    } finally {
      setUploadingSection(null);
    }
  };

  const exitManage = () => {
    setIsManaging(false);
    setEditingTitle(null);
    setEditingDoc(null);
    setConfirmDelete(null);
    cancelCreate();
  };

  const manageProps = {
    isManaging,
    confirmDelete,
    deletingSection,
    onRequestDeleteSection: setConfirmDelete,
    onCancelDeleteSection: () => setConfirmDelete(null),
    onDeleteSection: deleteSection,
    editingTitle,
    titleInput,
    onStartEditTitle: startEditTitle,
    onSaveTitle: saveTitle,
    onTitleInputChange: setTitleInput,
    editingDoc,
    docInput,
    onStartEditDoc: startEditDoc,
    onSaveDoc: saveDoc,
    onDocInputChange: setDocInput,
    onDeleteDoc: deleteDoc,
    onFileUpload: handleFileUpload,
    uploadingSection,
  };

  // ── Loading / error ──
  if (loadingPage) {
    return (
      <section className="edu-page">
        <div className="edu-loading">
          <FiLoader size={28} className="edu-spin" />
          <p>Carregando trilhas...</p>
        </div>
      </section>
    );
  }

  if (pageError) {
    return (
      <section className="edu-page">
        <div className="edu-page-error">
          <FiAlertCircle size={32} />
          <p>{pageError}</p>
          <button type="button" className="edu-empty-btn" onClick={fetchSections}>
            Tentar novamente
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="edu-page">
      {/* ── API error toast ── */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            className="edu-toast"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            <FiAlertCircle size={14} />
            {apiError}
            <button type="button" onClick={() => setApiError(null)}><FiX size={13} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Topbar ── */}
      <div className="edu-topbar">
        {isManaging && (
          <span className="edu-manage-hint">
            Clique nos ícones para criar, editar ou excluir trilhas e arquivos
          </span>
        )}
        {isManaging && (
          <button
            type="button"
            className="edu-new-section-btn"
            onClick={creating ? cancelCreate : openCreate}
          >
            {creating ? (
              <><FiX size={13} /> Cancelar</>
            ) : (
              <><FiPlus size={13} /> Nova trilha</>
            )}
          </button>
        )}
        <button
          type="button"
          className={`edu-manage-btn${isManaging ? " edu-manage-btn--active" : ""}`}
          onClick={isManaging ? exitManage : () => setIsManaging(true)}
        >
          {isManaging ? (
            <><FiCheck size={13} /> Concluir</>
          ) : (
            <><FiSettings size={13} /> Gerenciar</>
          )}
        </button>
      </div>

      {/* ── New section form ── */}
      <AnimatePresence initial={false}>
        {isManaging && creating && (
          <motion.div
            key="new-section-form"
            className="edu-new-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="edu-new-form-inner">
              <span className="edu-new-form-title">Nova trilha</span>

              <input
                autoFocus
                className="edu-new-input"
                placeholder="Nome da trilha"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") createSection(); }}
              />

              <textarea
                className="edu-new-input edu-new-textarea"
                placeholder="Descrição (opcional)"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />

              <div className="edu-new-row">
                <label className="edu-new-field">
                  <span>Categoria</span>
                  <select
                    className="edu-new-select"
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {categoryOptions.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value={NEW_CATEGORY}>+ Nova categoria</option>
                  </select>
                </label>

                <label className="edu-new-field">
                  <span>Nível</span>
                  <select
                    className="edu-new-select"
                    value={form.level}
                    onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}
                  >
                    {LEVELS.map((lvl) => (
                      <option key={lvl} value={lvl}>{lvl}</option>
                    ))}
                  </select>
                </label>
              </div>

              {form.category === NEW_CATEGORY && (
                <input
                  className="edu-new-input"
                  placeholder="Nome da nova categoria"
                  value={form.newCategory}
                  onChange={(e) => setForm((f) => ({ ...f, newCategory: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") createSection(); }}
                />
              )}

              <div className="edu-new-field">
                <span>Ícone</span>
                <div className="edu-icon-picker">
                  {ICON_OPTIONS.map((key) => {
                    const OptIcon = ICON_MAP[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`edu-icon-opt${form.iconName === key ? " edu-icon-opt--on" : ""}`}
                        onClick={() => setForm((f) => ({ ...f, iconName: key }))}
                        title={key}
                      >
                        <OptIcon size={15} />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="edu-new-actions">
                <button
                  type="button"
                  className="edu-new-cancel"
                  onClick={cancelCreate}
                  disabled={savingNew}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="edu-new-save"
                  onClick={createSection}
                  disabled={
                    savingNew ||
                    !form.name.trim() ||
                    (form.category === NEW_CATEGORY && !form.newCategory.trim())
                  }
                >
                  {savingNew ? (
                    <><FiLoader size={12} className="edu-spin" /> Criando...</>
                  ) : (
                    <><FiCheck size={12} /> Criar trilha</>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hint ── */}
      {(search.trim() || category !== "Todos") && (
        <p className="edu-hint">
          {visibleCount} trilha{visibleCount !== 1 ? "s" : ""}
          {category !== "Todos" && ` · ${category}`}
          {search.trim() && ` · "${search.trim()}"`}
        </p>
      )}

      {/* ── Featured ── */}
      {featured && (
        <FeaturedCard
          section={featured}
          expanded={expandedId === featured.id}
          onToggle={() => toggle(featured.id)}
          {...manageProps}
        />
      )}

      {/* ── Filters ── */}
      <div
        className="edu-filters"
        role="group"
        aria-label="Filtrar por categoria"
      >
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`edu-chip${category === cat ? " edu-chip--on" : ""}`}
            onClick={() => setCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── Grid header ── */}
      {grid.length > 0 && (
        <>
          {featured && (
            <div className="edu-section-header">
              <span className="edu-section-label">Todas as trilhas</span>
              <span className="edu-section-count">{grid.length}</span>
            </div>
          )}

          <div className="edu-grid">
            {grid.map((section, i) => (
              <ModuleCard
                key={section.id}
                section={section}
                index={i}
                expanded={expandedId === section.id}
                onToggle={() => toggle(section.id)}
                {...manageProps}
              />
            ))}
          </div>
        </>
      )}

      {/* ── Empty ── */}
      {!featured && grid.length === 0 && (
        <div className="edu-empty">
          <FiBookOpen size={36} className="edu-empty-icon" />
          <p className="edu-empty-title">Nenhuma trilha encontrada</p>
          <p className="edu-empty-sub">
            Tente ajustar o filtro ou o termo de busca.
          </p>
          {isManaging && !creating ? (
            <button type="button" className="edu-empty-btn" onClick={openCreate}>
              Criar trilha
            </button>
          ) : (
            <button
              type="button"
              className="edu-empty-btn"
              onClick={() => { setSearch(""); setCategory("Todos"); }}
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────
// Featured card
// ─────────────────────────────────────────────

function FeaturedCard({ section, expanded, onToggle, isManaging, ...manageProps }) {
  const { color, category, level, levelTone, name, description, docs, id, iconName } = section;
  const {
    editingTitle,
    titleInput,
    onStartEditTitle,
    onSaveTitle,
    onTitleInputChange,
    confirmDelete,
    deletingSection,
    onRequestDeleteSection,
    onCancelDeleteSection,
    onDeleteSection,
  } = manageProps;
  const Icon = ICON_MAP[iconName] ?? FiFileText;
  const showDocs = expanded || isManaging;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className={`edu-featured edu-featured--${color}${isManaging ? " edu-featured--managing" : ""}`}
    >
      <div className="edu-featured-top">
        <div className="edu-featured-icon-wrap">
          <Icon size={26} />
        </div>
        <div className="edu-featured-info">
          <div className="edu-badges">
            <span className="edu-badge edu-badge--start">⭐ Comece aqui</span>
            <span className="edu-badge edu-badge--cat">{category}</span>
            <LevelBadge level={level} tone={levelTone} />
          </div>

          {isManaging && editingTitle === id ? (
            <input
              autoFocus
              className="edu-title-edit-input"
              value={titleInput}
              onChange={(e) => onTitleInputChange(e.target.value)}
              onBlur={() => onSaveTitle(id)}
              onKeyDown={(e) => { if (e.key === "Enter") onSaveTitle(id); }}
            />
          ) : (
            <div className="edu-name-row">
              <h2 className="edu-featured-name">{name}</h2>
              {isManaging && (
                <>
                  <button type="button" className="edu-edit-name-btn" title="Editar título" onClick={() => onStartEditTitle(section)}>
                    <FiEdit2 size={13} />
                  </button>
                  <button
                    type="button"
                    className="edu-edit-name-btn edu-delete-section-btn"
                    title="Excluir trilha"
                    onClick={() => onRequestDeleteSection(id)}
                    disabled={deletingSection === id}
                  >
                    {deletingSection === id ? <FiLoader size={13} className="edu-spin" /> : <FiTrash2 size={13} />}
                  </button>
                </>
              )}
            </div>
          )}

          <p className="edu-featured-desc">{description}</p>
        </div>
      </div>

      {isManaging && confirmDelete === id && (
        <DeleteSectionConfirm
          name={name}
          onCancel={onCancelDeleteSection}
          onConfirm={() => onDeleteSection(id)}
        />
      )}

      {!isManaging && (
        <button
          type="button"
          className={`edu-expand-btn${expanded ? " edu-expand-btn--on" : ""}`}
          onClick={onToggle}
        >
          <span>{expanded ? "Ocultar conteúdo" : "Ver conteúdo"}</span>
          <motion.span className="edu-expand-chevron" animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.18 }}>
            <FiChevronDown size={15} />
          </motion.span>
        </button>
      )}

      <AnimatePresence initial={false}>
        {showDocs && (
          <motion.div
            key="featured-docs"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <DocList docs={docs} sectionId={id} isManaging={isManaging} {...manageProps} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// Module card
// ─────────────────────────────────────────────

function ModuleCard({ section, index, expanded, onToggle, isManaging, ...manageProps }) {
  const { color, category, level, levelTone, name, description, docs, id, iconName } = section;
  const {
    editingTitle,
    titleInput,
    onStartEditTitle,
    onSaveTitle,
    onTitleInputChange,
    confirmDelete,
    deletingSection,
    onRequestDeleteSection,
    onCancelDeleteSection,
    onDeleteSection,
  } = manageProps;
  const Icon = ICON_MAP[iconName] ?? FiFileText;
  const showDocs = expanded || isManaging;

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay: index * 0.04 }}
      className={`edu-card edu-card--${color}${isManaging ? " edu-card--managing" : ""}`}
    >
      <div className="edu-card-row">
        <div className="edu-card-icon">
          <Icon size={17} />
        </div>
        <div className="edu-card-content">
          {isManaging && editingTitle === id ? (
            <input
              autoFocus
              className="edu-title-edit-input edu-title-edit-input--card"
              value={titleInput}
              onChange={(e) => onTitleInputChange(e.target.value)}
              onBlur={() => onSaveTitle(id)}
              onKeyDown={(e) => { if (e.key === "Enter") onSaveTitle(id); }}
            />
          ) : (
            <div className="edu-name-row">
              <h3 className="edu-card-name">{name}</h3>
              {isManaging && (
                <>
                  <button type="button" className="edu-edit-name-btn edu-edit-name-btn--sm" title="Editar título" onClick={() => onStartEditTitle(section)}>
                    <FiEdit2 size={11} />
                  </button>
                  <button
                    type="button"
                    className="edu-edit-name-btn edu-edit-name-btn--sm edu-delete-section-btn"
                    title="Excluir trilha"
                    onClick={() => onRequestDeleteSection(id)}
                    disabled={deletingSection === id}
                  >
                    {deletingSection === id ? <FiLoader size={11} className="edu-spin" /> : <FiTrash2 size={11} />}
                  </button>
                </>
              )}
            </div>
          )}
          <p className="edu-card-desc">{description}</p>
          <div className="edu-badges">
            <LevelBadge level={level} tone={levelTone} />
            <span className="edu-badge edu-badge--cat">{category}</span>
          </div>
        </div>
      </div>

      {isManaging && confirmDelete === id && (
        <DeleteSectionConfirm
          name={name}
          onCancel={onCancelDeleteSection}
          onConfirm={() => onDeleteSection(id)}
        />
      )}

      {!isManaging && (
        <button
          type="button"
          className={`edu-expand-btn${expanded ? " edu-expand-btn--on" : ""}`}
          onClick={onToggle}
        >
          <span>{expanded ? "Ocultar conteúdo" : "Ver conteúdo"}</span>
          <motion.span className="edu-expand-chevron" animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.18 }}>
            <FiChevronDown size={15} />
          </motion.span>
        </button>
      )}

      <AnimatePresence initial={false}>
        {showDocs && (
          <motion.div
            key="card-docs"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <DocList
              docs={docs}
              sectionId={id}
              isManaging={isManaging}
              className="edu-doclist--card"
              {...manageProps}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

// ─────────────────────────────────────────────
// Delete section confirm
// ─────────────────────────────────────────────

function DeleteSectionConfirm({ name, onCancel, onConfirm }) {
  return (
    <div className="edu-delete-confirm">
      <FiAlertCircle size={14} className="edu-delete-confirm-icon" />
      <span className="edu-delete-confirm-text">
        Excluir <strong>{name}</strong> e todos os arquivos dela? Não é possível desfazer.
      </span>
      <div className="edu-delete-confirm-actions">
        <button type="button" className="edu-delete-confirm-cancel" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="edu-delete-confirm-ok" onClick={onConfirm}>
          <FiTrash2 size={12} /> Excluir
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Doc list
// ─────────────────────────────────────────────

function DocList({ docs, sectionId, isManaging, className, onFileUpload, uploadingSection, ...manageProps }) {
  const fileInputRef = useRef(null);
  const isUploading = uploadingSection === sectionId;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    onFileUpload(sectionId, file);
    e.target.value = "";
  };

  return (
    <div className={`edu-doclist${className ? ` ${className}` : ""}`}>
      {docs.length > 0 ? (
        docs.map((doc) => (
          <DocRow
            key={doc.id}
            doc={doc}
            sectionId={sectionId}
            isManaging={isManaging}
            {...manageProps}
          />
        ))
      ) : !isManaging ? (
        <p className="edu-doc-empty">Nenhum arquivo nesta trilha.</p>
      ) : null}

      {isManaging && (
        <div className="edu-add-file-row">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pptx,.ppt,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <button
            type="button"
            className={`edu-add-file-btn${isUploading ? " edu-add-file-btn--loading" : ""}`}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <><FiLoader size={12} className="edu-spin" /> Enviando...</>
            ) : (
              <><FiPlus size={12} /> Adicionar arquivo</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Doc row
// ─────────────────────────────────────────────

function DocRow({ doc, sectionId, isManaging, editingDoc, docInput, onStartEditDoc, onSaveDoc, onDocInputChange, onDeleteDoc }) {
  const typeLabel = doc.type ?? getTypeFromFileName(doc.originalName ?? "");

  if (isManaging) {
    return (
      <div className="edu-doc-row edu-doc-row--manage">
        <span className="edu-doc-text">
          {editingDoc === doc.id ? (
            <input
              autoFocus
              className="edu-doc-name-input"
              value={docInput}
              onChange={(e) => onDocInputChange(e.target.value)}
              onBlur={() => onSaveDoc(sectionId, doc.id)}
              onKeyDown={(e) => { if (e.key === "Enter") onSaveDoc(sectionId, doc.id); }}
            />
          ) : (
            <span className="edu-doc-name">{doc.displayName}</span>
          )}
          <span className="edu-doc-meta">{typeLabel}</span>
        </span>
        <div className="edu-doc-actions">
          <button type="button" className="edu-doc-action-btn" title="Renomear" onClick={() => onStartEditDoc(doc)}>
            <FiEdit2 size={12} />
          </button>
          <button type="button" className="edu-doc-action-btn edu-doc-action-btn--del" title="Remover" onClick={() => onDeleteDoc(sectionId, doc.id)}>
            <FiTrash2 size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <a href={doc.url} target="_blank" rel="noreferrer" download className="edu-doc-row">
      <span className="edu-doc-text">
        <span className="edu-doc-name">{doc.displayName}</span>
        <span className="edu-doc-meta">Arquivo local · disponivel</span>
        <span className={`edu-doc-badge edu-doc-badge--${typeLabel.toLowerCase()} w-fit`}>
          {typeLabel}
        </span>
      </span>
      <FiDownload size={13} className="edu-doc-dl" />
    </a>
  );
}

// ─────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────

function LevelBadge({ level, tone }) {
  return (
    <span className={`edu-badge edu-badge--level edu-badge--${tone}`}>
      {level}
    </span>
  );
}

function getTypeFromFileName(fileName) {
  const ext = (fileName.split(".").pop() ?? "").toLowerCase();
  if (ext === "ppt" || ext === "pptx") return "PPT";
  if (ext === "pdf") return "PDF";
  if (ext === "doc" || ext === "docx") return "DOC";
  if (ext === "xls" || ext === "xlsx") return "XLS";
  return ext.toUpperCase() || "ARQ";
}
